import React, { useEffect, useMemo, useContext } from 'react';
import { MembersStoreContext } from '../../Store/MembersStore';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { getUsers } from '../../Requests/UserService.ts';
import { Box, Typography, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add } from "@mui/icons-material";
import dayjs from 'dayjs';
import AddModalView from '../AddModalView/AddModalView';
import AddMember from "./AddMember";
import { VISIBLE_STATE_CASES, MEMBER_STATUS, MEMBER_TYPE_CASES, MEMBER_CATEGORY_CASES } from "../../Models/StateConstants.ts";
import { addPinControlsToColumns, createTogglePinHandler } from '../../Helpers/ColumnPinningHelper.jsx';
import { useTableState } from '../../Hooks/useTableState';
import { useFilteredBySearch } from '../../Hooks/useFilteredBySearch';
import { useScrollRestoration, createScrollContainerProps, createRowClickHandler } from '../../Hooks/useScrollRestoration';
import { useRefreshOnReturn } from '../../Hooks/useRefreshOnReturn';
import TableToolbarWithSearch from '../TableToolbar/TableToolbarWithSearch.jsx';
import ColumnsIcon from '../TableToolbar/ColumnsIcon.jsx';

const SEARCH_FIELD_OPTIONS = [
  { value: 'fullName', label: 'Name' },
  { value: 'organization', label: 'Organisation' },
  { value: 'position', label: 'Position' },
  { value: 'email', label: 'Email address' },
  { value: 'phoneNumber', label: 'Phone number' },
]

/**
 * Highlights matching text in a string
 * @param {string} text - The text to highlight in
 * @param {string} query - The search query to highlight
 * @returns {JSX.Element} - Text with highlighted matches
 */
function highlightText(text, query) {
  if (!text || !query || typeof text !== 'string') return text

  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'))
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} style={{ backgroundColor: '#fff59d', padding: '0 2px' }}>{part}</mark>
        ) : (
          part
        )
      )}
    </>
  )
}

const BASE_COLUMNS = [
  {
    accessorFn: (originalRow) => originalRow.name + " " + originalRow.surname,
    id: 'fullName',
    header: 'Name',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'organization',
    header: 'Organisation',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'position',
    header: 'Position',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'email',
    header: 'Email address',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'phoneNumber',
    header: 'Phone number',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'gender',
    header: 'Gender',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: ['male', 'female'],
    Cell: ({ renderedCellValue }) =>
      renderedCellValue ? renderedCellValue.charAt(0).toUpperCase() + renderedCellValue.slice(1) : null
  },
  {
    accessorKey: 'memberType',
    header: 'Member Type',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: MEMBER_TYPE_CASES.map((value) => value.label),
    Cell: ({ renderedCellValue, row }) => renderedCellValue === null ? (<></>) : (<Chip label={renderedCellValue} />)
  },
  {
    accessorKey: 'memberStatus',
    header: 'Member Status',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: [MEMBER_STATUS.ACTIVE, MEMBER_STATUS.INACTIVE, MEMBER_STATUS.SUSPENDED],
    Cell: ({ renderedCellValue, row }) => (
      getChipForMemberStatus(renderedCellValue)
    )
  },
  {
    accessorKey: 'memberCategory',
    header: 'Member Category',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: MEMBER_CATEGORY_CASES.map((value) => value.label),
    Cell: ({ renderedCellValue, row }) => renderedCellValue === null ? (<></>) : (<Chip label={renderedCellValue} />)
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.membershipValidStart),
    accessorKey: 'membershipValidStart',
    header: 'Mem. Start Date',
    filterVariant: 'date-range',
    enableGlobalFilter: false,
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.membershipValidUntil),
    accessorKey: 'membershipValidUntil',
    header: 'Mem. Renewal Date',
    filterVariant: 'date-range',
    enableGlobalFilter: false,
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
  {
    accessorKey: 'hiddenState',
    header: 'Visibility',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
    Cell: ({ cell }) => (
      <Chip label={cell.getValue()} />
    )
  },
]

export function getChipForMemberStatus(status) {
  switch (status) {
    case MEMBER_STATUS.ACTIVE:
      return <Chip
        label={status}
        color="success"
      />
    case MEMBER_STATUS.INACTIVE:
      return <Chip
        label={status}
        color="default"
      />
    case MEMBER_STATUS.SUSPENDED:
      return <Chip
        label={status}
        color="error"
      />
    default:
      return
  }
}

export default function Members() {

  const store = useContext(MembersStoreContext)
  const navigate = useNavigate()

  // Use custom hooks for state management
  const [columnVisibility, setColumnVisibility] = useTableState(store, 'columnVisibility', {})
  const [columnFilters, setColumnFilters] = useTableState(store, 'columnFilters', [])
  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [pagination, setPagination] = useTableState(store, 'pagination', { pageIndex: 0, pageSize: 10 })
  const [columnPinning, setColumnPinning] = useTableState(store, 'columnPinning', { left: [] })
  const [open, setOpen] = useTableState(store, 'open', false)
  const [isLoading, setIsloading] = useTableState(store, 'isLoading', true)
  const [isRefetching, setIsRefetching] = useTableState(store, 'isRefetching', false)
  const [allTableData, setAllTableData] = useTableState(store, 'allTableData', [])
  const [tableData, setTableData] = useTableState(store, 'tableData', [])
  const [selectedChip, setSelectedChip] = useTableState(store, 'selectedChip', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', 'fullName')

  // Scroll restoration hook
  const tableContainerRef = useScrollRestoration(store, tableData, isLoading)

  useEffect(() => {
    setSelectedChip(0)
  }, [setSelectedChip])

  useEffect(() => {
    switch (selectedChip) {
      case 0:
        setTableData(allTableData.filter((data) => data.memberType === "Member"))
        break
      case 1:
        setTableData(allTableData.filter((data) => data.memberType === "Prospect"))
        break
      case 2:
        setTableData(allTableData.filter((data) => data.memberType === "Draft"))
        break
      default:
        setTableData(allTableData)
        break
    }
  }, [selectedChip, allTableData, setTableData])

  const fetchData = async () => {
    const result = await getUsers()
    setIsloading(false)
    setIsRefetching(false)
    setAllTableData(result)
  }

  useRefreshOnReturn(
    store,
    store.allTableData,
    () => {
      setAllTableData(store.allTableData)
      setTableData(store.tableData)
    },
    fetchData,
    setIsloading,
    setIsRefetching
  )

  const columns = useMemo(() => {
    return BASE_COLUMNS.map((col) => {
      if (col.id === 'fullName' && searchField === 'fullName' && globalFilter) {
        return {
          ...col,
          Cell: ({ renderedCellValue, row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <img
                alt="avatar"
                height={32}
                width={32}
                src={row.original.avatar}
                loading="lazy"
                style={{ borderRadius: '50%' }}
              />
              <span>{highlightText(renderedCellValue, globalFilter)}</span>
            </Box>
          )
        }
      }
      if (col.accessorKey === 'organization' && searchField === 'organization' && globalFilter) {
        return {
          ...col,
          Cell: ({ renderedCellValue }) => <span>{highlightText(renderedCellValue, globalFilter)}</span>
        }
      }
      if (col.accessorKey === 'position' && searchField === 'position' && globalFilter) {
        return {
          ...col,
          Cell: ({ renderedCellValue }) => <span>{highlightText(renderedCellValue, globalFilter)}</span>
        }
      }
      if (col.accessorKey === 'email' && searchField === 'email' && globalFilter) {
        return {
          ...col,
          Cell: ({ renderedCellValue }) => <span>{highlightText(renderedCellValue, globalFilter)}</span>
        }
      }
      if (col.accessorKey === 'phoneNumber' && searchField === 'phoneNumber' && globalFilter) {
        return {
          ...col,
          Cell: ({ renderedCellValue }) => <span>{highlightText(renderedCellValue, globalFilter)}</span>
        }
      }
      // Default cell for fullName when not searching
      if (col.id === 'fullName' && !col.Cell) {
        return {
          ...col,
          Cell: ({ renderedCellValue, row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <img
                alt="avatar"
                height={32}
                width={32}
                src={row.original.avatar}
                loading="lazy"
                style={{ borderRadius: '50%' }}
              />
              <span>{renderedCellValue}</span>
            </Box>
          )
        }
      }
      return col
    })
  }, [searchField, globalFilter])
  const getToggleHandler = useMemo(() => createTogglePinHandler(setColumnPinning), [setColumnPinning])
  const columnsWithPin = useMemo(() => {
    return addPinControlsToColumns(columns, columnPinning, getToggleHandler)
  }, [columns, columnPinning, getToggleHandler])

  const getRowSearchValue = useMemo(
    () => (row, field) =>
      field === 'fullName' ? [row.name, row.surname].filter(Boolean).join(' ') : row[field],
    []
  )
  const data = useFilteredBySearch(tableData, globalFilter, searchField, getRowSearchValue)

  const table = useMaterialReactTable({
    columns: columnsWithPin,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnPinning: true,
    enableGlobalFilter: false,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      columnPinning: columnPinning,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    muiFilterDatePickerProps: {
      format: 'DD/MM/YYYY',
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: createRowClickHandler(store, tableContainerRef, () => navigate(`/member/${row.original.objectId}`)),
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        chips={[
          {
            key: 'members',
            label: 'Members ' + allTableData.filter((data) => data.memberType === 'Member').length,
            active: selectedChip === 0,
            onClick: () => setSelectedChip(selectedChip === 0 ? undefined : 0),
          },
          {
            key: 'prospects',
            label: 'Prospects ' + allTableData.filter((data) => data.memberType === 'Prospect').length,
            active: selectedChip === 1,
            onClick: () => setSelectedChip(selectedChip === 1 ? undefined : 1),
          },
          {
            key: 'drafts',
            label: 'Drafts ' + allTableData.filter((data) => data.memberType === 'Draft').length,
            active: selectedChip === 2,
            onClick: () => setSelectedChip(selectedChip === 2 ? undefined : 2),
          },
        ]}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={100}
        exportFileName="members"
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add Member
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ...createScrollContainerProps(store, tableContainerRef),
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      }
    }
  })

  return (
    <>
      <Box
        paddingTop={2}
        paddingBottom={1}
        display="flex"
        justifyContent="space-between"
      >
        <Typography
          variant="h4"
        >
          Members
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Member"}
        view={
          <AddMember publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
